import React, { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import Loading from '../loading'
import { uploadFile } from '../../common/fileUpload'

import S from './styled'

const maxVideoWidth = 1920
const maxVideoHeight = 1200
const maxVideoSizeMB = 20
const maxVideoSize = maxVideoSizeMB * 1024 * 1024 //20MB

const maxImageWidth = 1920
const maxImageHeight = 1200
const maxImageSizeKB = 500
const maxImageSize = maxImageSizeKB * 1024 //500KB

const showError = message => toast.error(
  message ||
  'Your file size exceeds the specified restrictions, please refer to the appropriate file size and dimensions.', {
    position: toast.POSITION.TOP_RIGHT,
  })

const URL = window.webkitURL || window.URL

export const getVideoDimensions = file => new Promise((resolve, reject) => {
  let videoTag = document.createElement('video')
  videoTag.addEventListener('loadedmetadata', () => {
    const dimensions = {
      width: videoTag.videoWidth, height: videoTag.videoHeight,
    }
    URL.revokeObjectURL(videoTag.src)
    document.body.removeChild(videoTag)
    resolve(dimensions)
  })
  videoTag.src = URL.createObjectURL(file)
  videoTag.style = 'display: none;'
  document.body.appendChild(videoTag)
})

export const getImageDimensions = file => new Promise((resolve, reject) => {
  let img = document.createElement('img')
  img.onload = function(e) {
    const dimensions = {
      width: img.width, height: img.height,
    }
    URL.revokeObjectURL(img.src)
    document.body.removeChild(img)
    resolve(dimensions)
  }
  img.src = URL.createObjectURL(file)
  img.style = 'display: none;'
  document.body.appendChild(img)
})

const ImageUpload = ({
  name, value, onChange,
  width, height, mobileWidth, mobileHeight,
  title, style,
  error,
  allowVideo,
  noStretch,
  noBorder,
  readOnly,
  optional,
}) => {

  const [uploading, setUploading] = useState(false)

  const handleFileUpload = useCallback(e => {
    (async() => {
      setUploading(true)
      const files = e.target.files
      if (files && files[0]) {
        const file = files[0]
        if (file.type.includes('video')){
          const dimentions = await getVideoDimensions(file)
          if (dimentions.width > maxVideoWidth || dimentions.height > maxVideoHeight || file.size > maxVideoSize) {
            setUploading(false)
            return showError()
          }
        } else {
          const dimentions = await getImageDimensions(file)
          if (dimentions.width > maxImageWidth || dimentions.height > maxImageHeight || file.size > maxImageSize) {
            setUploading(false)
            return showError()
          }
        }

        try {
          const { url } = await uploadFile(files[0])
          onChange({
            target: {
              name, value: url, type: 'image',
            },
          })
        } finally {
          setUploading(false)
        }
      }
    })()
  }, [name, onChange])

  const handleClear = useCallback(e => {
    onChange({
      target: {
        name, value: undefined, type: 'image',
      },
    })
  }, [name, onChange])

  const inputEl = useRef(null)

  const handleClick = useCallback(() => {
    inputEl.current.click()
  }, [inputEl])

  const isVideo = value && value.toLocaleLowerCase().endsWith('.mp4')

  return (
    <S.ImageUpload style={style}>
      <S.Header>
        {title && <label>{title}</label>}
        <S.FileParams>
          {
            allowVideo ?
              `(max. ${maxImageWidth} x ${maxImageHeight} px / Video ${maxVideoSizeMB}MB / Image ${maxImageSizeKB}kb)` :
              `(max. ${maxImageWidth} x ${maxImageHeight} px / ${maxImageSizeKB}kb)`
          }
        </S.FileParams>
      </S.Header>
      {
        uploading ? (
          <S.ImageContainer
            width={width} height={height}
            mobileWidth={mobileWidth} mobileHeight={mobileHeight}
            noBorder={noBorder}
            readOnly={readOnly}
          >
            <Loading />
          </S.ImageContainer>
        ) : (
          <S.ImageContainer
            onClick={readOnly ? null : handleClick}
            width={width} height={height}
            mobileWidth={mobileWidth} mobileHeight={mobileHeight}
            className={error ? 'error' : ''}
            noBorder={noBorder}
            readOnly={readOnly}
            style={
              value && !isVideo? {
                backgroundImage: `url('${value}')`,
                backgroundSize: noStretch ? 'contain' : 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              } : {}
            }>
            {
              isVideo && (
                <video autoPlay loop src={value} style={{
                  width: '100%',
                  height: '100%',
                }}/>
              )
            }
            <input
              type="file"
              id={name}
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              ref={inputEl}
              accept={allowVideo ? 'image/*,video/mp4,video/x-m4v,video/*' : 'image/*' }
            />
            {
              !value && (
                <S.UploadPlaceholder>
                  {
                    readOnly ? 'No image' : `Click to upload image ${allowVideo ? 'or video': ''}`
                  }
                </S.UploadPlaceholder>
              )
            }
          </S.ImageContainer>
        )
      }
      {
        !readOnly && optional && value && (
          <S.DeleteImage onClick={handleClear}>&times;&nbsp;clear</S.DeleteImage>
        )
      }
      {
        error && <S.ErrorLabel>{error}</S.ErrorLabel>
      }
    </S.ImageUpload>
  )
}

export default ImageUpload
